import { createAsyncThunk } from "@reduxjs/toolkit"
import axios from "../../utils/axios"
import type { IError } from "../../utils/api.types"
import type { ITdsReceivableResponse, ITdsReceivableQueryParams } from "../../utils/tdsReceivable.types"

/**
 * Fetch TDS Receivable data async thunk.
 */
export const fetchTdsReceivable = createAsyncThunk<
  ITdsReceivableResponse,
  ITdsReceivableQueryParams,
  {
    rejectValue: IError
  }
>("tdsReceivable/fetchTdsReceivable", async (queryParams: ITdsReceivableQueryParams, thunkApi) => {
  try {
    // Build query string from parameters
    const params = new URLSearchParams()

    if (queryParams.page) params.append("page", queryParams.page.toString())
    if (queryParams.limit) params.append("limit", queryParams.limit.toString())
    if (queryParams.start) params.append("start", queryParams.start)
    if (queryParams.end) params.append("end", queryParams.end)
    if (queryParams.filter) params.append("filter", queryParams.filter)
    if (queryParams.voucherType && queryParams.voucherType !== "all") {
      params.append("voucherType", queryParams.voucherType)
    }
    if (queryParams.status && queryParams.status !== "all") {
      params.append("status", queryParams.status)
    }
    if (queryParams.amountStartRange) {
      params.append("amountStartRange", queryParams.amountStartRange.toString())
    }
    if (queryParams.amountEndRange) {
      params.append("amountEndRange", queryParams.amountEndRange.toString())
    }

    const queryString = params.toString()
    const url = `/api/v1/invoice/tds-receivable${queryString ? `?${queryString}` : ""}`

    const response = await axios.get(url)

    if (response.status === 200) {
      // Map API response to expected shape
      // Map summary keys to match interface
      const summary = response.data.summary
        ? {
            ...response.data.summary,
            appliedAmountTotal: response.data.summary.totalAppliedAmount ?? null,
          }
        : null

      return {
        invoices: response.data.applications || [],
        summary,
        total: response.data.total || 0,
      }
    }

    return thunkApi.rejectWithValue({
      message: "Failed to fetch TDS receivable data",
    })
  } catch (error: any) {
    return thunkApi.rejectWithValue({
      message: error.response?.data?.message || "An error occurred while fetching TDS receivable data",
    })
  }
})

/**
 * Export TDS Receivable data async thunk.
 */
export const exportTdsReceivable = createAsyncThunk<
  Blob,
  ITdsReceivableQueryParams,
  {
    rejectValue: IError
  }
>("tdsReceivable/exportTdsReceivable", async (queryParams: ITdsReceivableQueryParams, thunkApi) => {
  try {
    // Build query string from parameters
    const params = new URLSearchParams()

    if (queryParams.start) params.append("start", queryParams.start)
    if (queryParams.end) params.append("end", queryParams.end)
    if (queryParams.filter) params.append("filter", queryParams.filter)
    if (queryParams.voucherType && queryParams.voucherType !== "all") {
      params.append("voucherType", queryParams.voucherType)
    }
    if (queryParams.status && queryParams.status !== "all") {
      params.append("status", queryParams.status)
    }
    if (queryParams.amountStartRange) {
      params.append("amountStartRange", queryParams.amountStartRange.toString())
    }
    if (queryParams.amountEndRange) {
      params.append("amountEndRange", queryParams.amountEndRange.toString())
    }

    const queryString = params.toString()
    const url = `/api/v1/invoice/tds-receivable/export${queryString ? `?${queryString}` : ""}`

    const response = await axios.get(url, {
      responseType: "blob",
    })

    if (response.status === 200) {
      return response.data
    }

    return thunkApi.rejectWithValue({
      message: "Failed to export TDS receivable data",
    })
  } catch (error: any) {
    return thunkApi.rejectWithValue({
      message: error.response?.data?.message || "An error occurred while exporting TDS receivable data",
    })
  }
})
